import React, { useState, useRef, useEffect, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrashAlt,
  faGripLines,
  faCog,
  faArrowUp,
  faArrowDown,
  faEye,
  faClipboard,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { ListGroup } from "react-bootstrap";
import HoverDropdown from "./HoverDropdown";
import RowColumnProperties from "./RowColumnProperties";
import { FormattableInput } from "./FormattableInput";
import "./styles/RowColumnComponent.css";
import VariableContext from "../contexts/VariableContext";
import FetchingFreq from "../contexts/FetchingFreq";
import CommonButton from "../elments/CommonButton";
import PasteModal from "./PasteModal";
import { useToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { useEscapeKey } from "./hooks/useEscapeKey";

// Question types this component knows type-specific row/column properties
// for. Any other qType (Intro, loop, loopend, SingleGridSlider, ...) falls
// outside that list, so every property stays available for it rather than
// guessing at rules that were never defined for it.
const ROW_PROPERTY_RECOGNIZED_QTYPES = [
  "SingleResponse", "MultipleResponse", "String", "StringGrid",
  "Numeric", "NumericGrid", "SingleGrid", "MultiGrid", "CustomRows", "CustomCols",
];

// `qtypes` restricts a property to specific question types (evidence-based,
// same reasoning as OESettingsComponent's TYPE_SCOPED_PROPERTIES); omitting
// it means Common - available for every question type.
const AVAILABLE_PROPERTIES = [
  { key: 'filterLogic', label: 'Filter Logic', type: 'text', width: 150 },
  { key: 'termLogic', label: 'Term Logic', type: 'text', width: 150 },
  { key: 'punchLogic', label: 'Punch Logic', type: 'text', width: 150 },
  { key: 'quotaCounts', label: 'Quota Counts', type: 'text', width: 120 },
  // "Exclusive" (e.g. "None of these") only means something when more than
  // one option can be picked.
  { key: 'exclusive', label: 'Exclusive', type: 'checkbox', width: 78, qtypes: ['MultipleResponse', 'MultiGrid'] },
  { key: 'fix', label: 'Fix', type: 'checkbox', width: 56 },
  // "Other, please specify" is a choice-based concept - not applicable to
  // plain String/Numeric questions.
  { key: 'other', label: 'Other', type: 'checkbox', width: 60, qtypes: ['SingleResponse', 'MultipleResponse', 'SingleGrid', 'MultiGrid'] },
  { key: 'otherPiping', label: 'Other Piping', type: 'text', width: 140, qtypes: ['SingleResponse', 'MultipleResponse', 'SingleGrid', 'MultiGrid'] },
  // "Other, please specify" text box settings - see the matching "Other
  // (Specify) Settings" block in RowColumnProperties.js's Behavior section,
  // gated the same way on item.other. otherRequired defaults CHECKED
  // (defaultTrue below) when absent, matching that popup's own default.
  { key: 'otherRequired', label: 'Other Required', type: 'checkbox', width: 90, qtypes: ['SingleResponse', 'MultipleResponse', 'SingleGrid', 'MultiGrid'], defaultTrue: true },
  // Same dropdown as the "Other (Specify) Settings" block in
  // RowColumnProperties.js - a real <select> here (not a free-text column)
  // so the value always matches one of the input types that box actually
  // supports.
  {
    key: 'otherInputType', label: 'Other Input Type', type: 'select', width: 130,
    qtypes: ['SingleResponse', 'MultipleResponse', 'SingleGrid', 'MultiGrid'],
    options: [
      { value: 'text', label: 'Text' },
      { value: 'email', label: 'Email' },
      { value: 'password', label: 'Password' },
      { value: 'tel', label: 'Mobile Number' },
      { value: 'url', label: 'URL' },
      { value: 'date', label: 'Date' },
    ],
  },
  // Pretext/Posttext/Min/Max here are the CustomRows-only fields (see the
  // matching "Custom Row Settings" block in RowColumnProperties.js, gated
  // the same way on qType === "CustomRows").
  { key: 'Pretext', label: 'Pretext', type: 'text', width: 120, qtypes: ['CustomRows'] },
  { key: 'Posttext', label: 'Posttext', type: 'text', width: 120, qtypes: ['CustomRows'] },
  { key: 'min', label: 'Min', type: 'text', width: 72, qtypes: ['CustomRows'] },
  { key: 'max', label: 'Max', type: 'text', width: 72, qtypes: ['CustomRows'] },
  // Per-response custom color (hex). Common - any answer option on any
  // question type can be given its own color, same as in RowColumnProperties.js.
  { key: 'color', label: 'Color', type: 'color', width: 130 },
];

// Same shape the color picker/manual hex field in RowColumnProperties.js
// accept - kept in sync so a value typed/pasted in either place is valid in
// both.
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const normalizeHexForPicker = (hex) => {
  if (!HEX_COLOR_REGEX.test(hex || '')) return '#ffffff';
  if (hex.length === 4) {
    return '#' + hex.slice(1).split('').map((c) => c + c).join('');
  }
  return hex;
};

// Common properties (no `qtypes`) are always available. Type-scoped ones
// only show up for the qTypes listed - unless qType itself falls outside
// ROW_PROPERTY_RECOGNIZED_QTYPES, in which case nothing is hidden.
const normalizeType = (type) => String(type || "").replace(/\s+/g, "").toLowerCase();
const typeMatches = (a, b) => normalizeType(a) === normalizeType(b);

const isColumnPropVisible = (prop, qType) => {
  if (!prop.qtypes) return true;
  if (!ROW_PROPERTY_RECOGNIZED_QTYPES.some((t) => typeMatches(t, qType))) return true;
  return prop.qtypes.includes(qType);
};

// Format a 0-based indexes array as a human-readable, 1-based range list
// (e.g. [2,3,4,8] -> "3-5, 9"). Kept in sync with the parser in
// RowColumnProperties.js, which turns that same text back into indexes.
const formatRowNumbers = (indexes) => {
  if (!indexes || indexes.length === 0) return "(none)";
  const sorted = [...indexes].sort((a, b) => a - b);
  const parts = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (let i = 1; i <= sorted.length; i++) {
    const cur = sorted[i];
    if (cur === prev + 1) {
      prev = cur;
      continue;
    }
    parts.push(start === prev ? `${start + 1}` : `${start + 1}-${prev + 1}`);
    start = cur;
    prev = cur;
  }
  return parts.join(", ");
};

const RowColumnComponent = ({
  items,
  setItems,
  deleteRow,
  label,
  handlePaste,
  addList,
  addadditionalRow,
  list,
  savePropertyChanges,
  screenOutSelections,
  onScreenOutChange,
  qType,
  selectedVariable,
  setList = setList
}) => {
  const { selectedTitle } = useContext(VariableContext);
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemIndex, setItemIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasteModalOpen, setIsPasteModalOpen] = useState(false);
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);
  const [currentListId, setCurrentListId] = useState(null);
  const [pendingPaste, setPendingPaste] = useState(null);
  const [preserveOriginalIndices, setPreserveOriginalIndices] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [columnPasteModal, setColumnPasteModal] = useState(null);
  const [columnPasteContent, setColumnPasteContent] = useState('');
  // Add ref for the main container to handle paste events
  const containerRef = useRef(null);
  const configRef = useRef(null);

  const columnPasteModalRef = useRef(null);

  const INDENT_PX = 22; // per nesting level, for both header rows and the real rows under them

  // Headers are first-class entries inside `items` itself (header: true),
  // not a separate sibling array. "Real" rows/columns are everything else in
  // `items`. A header's membership is an explicit `indexes` array — 0-based
  // positions among the REAL (non-header) items only, not necessarily
  // contiguous — so they stay meaningful no matter where the header entry
  // physically sits in the array. Headers are always written back at the
  // tail of `items` on save; their own position is irrelevant since
  // `indexes` is authoritative. Out-of-range/duplicate positions are dropped
  // on read so deletes/reorders can never point out of range.
  // Headers can nest: one header's indexes can be a subset of another's —
  // that containment is what drives the indentation shown in the list.
  const headersEnabled = label === "Rows"; // headers are only offered for Rows
  // Split `items` into real rows and headers in a single pass, capturing
  // each header's raw array position (rawIndex) directly on the object.
  // Resolving rawIndex this way — rather than via an id-keyed lookup — means
  // it can never collide even if a header temporarily lacks an id (legacy
  // data, before the repair effect above has run).
  const realItems = [];
  const headersRaw = [];
  (items || []).forEach((it, rawIndex) => {
    if (it?.header) {
      headersRaw.push({ it, rawIndex });
    } else {
      realItems.push(it);
    }
  });
  const itemCount = realItems.length;
  const headers = headersRaw.map(({ it: h, rawIndex }) => {
    const raw = Array.isArray(h.indexes) ? h.indexes : [];
    const indexes = [...new Set(
      raw.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n >= 0 && n < itemCount)
    )].sort((a, b) => a - b);
    return { ...h, indexes, rawIndex };
  });

  const saveHeaders = (next) => {
    savePropertyChanges([...realItems, ...next], label);
  };

  // Every item (row or header) needs a stable id once it can be edited via
  // the properties modal — edits are matched by id rather than raw array
  // position, since position can drift between when the modal opened and
  // when a field is committed (reorders, other edits, header conversions).
  const ensureId = (it) => it?.id || `it_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // One-time repair pass for header rows created by an earlier version of
  // this feature: rows marked header:true with no id at all (the original
  // "Header" checkbox, before headers had ids), or with a legacy
  // start/end range instead of an indexes array (the version before this
  // one). Runs whenever `items` changes; becomes a no-op the moment nothing
  // needs fixing, so it can't loop.
  useEffect(() => {
    const list = items || [];
    const needsFix = list.some(
      (it) => it?.header && (!it.id || (!Array.isArray(it.indexes) && (it.start !== undefined || it.end !== undefined)))
    );
    if (!needsFix) return;
    const fixed = list.map((it) => {
      if (!it?.header) return it;
      const patch = {};
      if (!it.id) patch.id = `hdr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      if (!Array.isArray(it.indexes) && (it.start !== undefined || it.end !== undefined)) {
        const s = Number(it.start) || 0;
        const e = Number(it.end) || s;
        const lo = Math.min(s, e), hi = Math.max(s, e);
        const idx = [];
        for (let i = lo; i <= hi; i++) idx.push(i);
        patch.indexes = idx;
      }
      return Object.keys(patch).length ? { ...it, ...patch } : it;
    });
    savePropertyChanges(fixed, label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Used only to pick a sensible default position for a *new* header —
  // prefers a row that isn't under any header yet. Nesting is allowed, so
  // this is just a convenience default, not a hard constraint.
  const buildCoverage = () => {
    const cov = new Array(itemCount).fill(false);
    headers.forEach((h) => h.indexes.forEach((i) => { cov[i] = true; }));
    return cov;
  };
  const firstFreeRow = () => buildCoverage().findIndex((c) => !c);
  const canAddHeader = headersEnabled && itemCount > 0;

  const addHeader = () => {
    if (itemCount === 0) {
      toast?.error?.("Add rows before creating a header");
      return;
    }
    const free = firstFreeRow();
    const start = free === -1 ? 0 : free;
    // Default label is h1, h2, ... based on how many headers already exist,
    // editable by the user afterwards. Defaults to covering a single row —
    // open its properties right away so its row list can be set precisely.
    const newHeader = {
      id: ensureId(),
      header: true,
      text: `h${headers.length + 1}`,
      indexes: [start],
    };
    const newRawIndex = (items || []).length; // headers are appended at the tail
    saveHeaders([...headers, newHeader]);
    openPropertiesModal(newHeader, newRawIndex);
  };

  const deleteHeader = (id) => {
    saveHeaders(headers.filter((h) => h.id !== id));
  };

  // Update a header by id (not raw array position) so a header row can
  // always be edited reliably regardless of where it currently sits.
  const updateHeaderField = (id, patch) => {
    const updatedItems = (items || []).map((it) => (it?.header && it.id === id ? { ...it, ...patch } : it));
    savePropertyChanges(updatedItems, label);
  };

  // Load saved list selection for this question and list type
  useEffect(() => {
    if (selectedTitle) {
      const savedSelections = JSON.parse(localStorage.getItem('listSelections') || '{}');
      const questionSelections = savedSelections[selectedTitle];
      if (questionSelections) {
        const listId = questionSelections[label.toLowerCase()];
        if (listId) {
          setCurrentListId(listId);
        }
      }
    }
  }, [selectedTitle, label]);

  // Auto-detect if current items match any list
  useEffect(() => {
    if (items && items.length > 0 && list.length > 0) {
      const currentContent = JSON.stringify(items);
      const matchingList = list.find(listItem =>
        JSON.stringify(listItem[Object.keys(listItem)[0]]) === currentContent
      );

      if (matchingList) {
        setCurrentListId(matchingList.id);
        saveListSelection(matchingList.id);
      } else if (currentListId) {
        // Current items don't match the selected list - clear selection
        setCurrentListId(null);
        clearListSelection();
      }
    }
  }, [items, list]);

  // Load visible columns for this question+label from localStorage
  useEffect(() => {
    if (selectedTitle) {
      const saved = JSON.parse(localStorage.getItem('rowColumnColumns') || '{}');
      const k = `${selectedTitle}_${label}`;
      setVisibleColumns(saved[k] || []);
    }
  }, [selectedTitle, label]);

  // Keep ref in sync so handleGlobalPaste can see current column paste modal state
  useEffect(() => {
    columnPasteModalRef.current = columnPasteModal;
  }, [columnPasteModal]);

  // Escape closes whichever of this component's own popups is open - same
  // handler its own Cancel/close button already uses.
  useEscapeKey(configOpen, () => setConfigOpen(false));
  useEscapeKey(!!columnPasteModal, () => { setColumnPasteModal(null); setColumnPasteContent(''); });

  // Close configure dropdown on outside click
  useEffect(() => {
    if (!configOpen) return;
    const handler = (e) => {
      if (configRef.current && !configRef.current.contains(e.target)) {
        setConfigOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [configOpen]);

  // Save list selection to localStorage
  const saveListSelection = (listId) => {
    if (!selectedTitle) return;

    const savedSelections = JSON.parse(localStorage.getItem('listSelections') || '{}');
    if (!savedSelections[selectedTitle]) {
      savedSelections[selectedTitle] = {};
    }
    savedSelections[selectedTitle][label.toLowerCase()] = listId;
    localStorage.setItem('listSelections', JSON.stringify(savedSelections));
  };

  // Clear list selection from localStorage
  const clearListSelection = () => {
    if (!selectedTitle) return;

    const savedSelections = JSON.parse(localStorage.getItem('listSelections') || '{}');
    if (savedSelections[selectedTitle]) {
      delete savedSelections[selectedTitle][label.toLowerCase()];
      localStorage.setItem('listSelections', JSON.stringify(savedSelections));
    }
  };

  const saveVisibleColumns = (cols) => {
    if (!selectedTitle) return;
    const saved = JSON.parse(localStorage.getItem('rowColumnColumns') || '{}');
    saved[`${selectedTitle}_${label}`] = cols;
    localStorage.setItem('rowColumnColumns', JSON.stringify(saved));
  };

  const toggleColumn = (propKey) => {
    const newCols = visibleColumns.includes(propKey)
      ? visibleColumns.filter(k => k !== propKey)
      : [...visibleColumns, propKey];
    setVisibleColumns(newCols);
    saveVisibleColumns(newCols);
  };

  // Properties offered in the "Columns" popup for the current question type.
  const availablePropertiesForType = AVAILABLE_PROPERTIES.filter((p) => isColumnPropVisible(p, qType));
  // ...and the subset of those the user has actually turned on - used to
  // render the header row and the per-row inline cells. Intersecting with
  // availablePropertiesForType (not just visibleColumns) means a column
  // toggled on for e.g. a CustomRows question disappears on its own if the
  // question type is switched away, instead of lingering as a dead column.
  const visibleColumnDefs = availablePropertiesForType.filter((p) => visibleColumns.includes(p.key));

  const handleInlinePropertyChange = (index, property, value) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [property]: value };
    savePropertyChanges(updatedItems, label);
  };

  // When pasting multi-line data directly into an inline cell, distribute each
  // line to the corresponding row starting at startIndex (spreadsheet behaviour).
  const handleInlinePaste = (e, startIndex, propKey) => {
    const plain = e.clipboardData.getData('text/plain');
    const lines = plain.split(/\r?\n/).map(l => l.trim()).filter(l => l !== '');
    if (lines.length <= 1) return; // single value — let the default input behaviour handle it
    e.preventDefault();
    const updatedItems = [...(items || [])];
    lines.forEach((line, i) => {
      const target = startIndex + i;
      if (target < updatedItems.length) {
        updatedItems[target] = { ...updatedItems[target], [propKey]: line };
      }
    });
    savePropertyChanges(updatedItems, label);
    const filled = Math.min(lines.length, (items || []).length - startIndex);
    toast.success(`${propKey} pasted to ${filled} row${filled !== 1 ? 's' : ''}`);
  };

  const applyColumnPaste = () => {
    if (!columnPasteModal || !columnPasteContent.trim()) return;
    const lines = columnPasteContent.split('\n').map(l => l.trim());
    const prop = columnPasteModal;
    const updatedItems = (items || []).map((item, index) => {
      if (index >= lines.length || lines[index] === '') return item;
      if (prop.type === 'checkbox') {
        const v = lines[index].toLowerCase();
        return { ...item, [prop.key]: v === 'true' || v === '1' || v === 'yes' || v === 'x' };
      }
      return { ...item, [prop.key]: lines[index] };
    });
    savePropertyChanges(updatedItems, label);
    const filled = lines.filter(l => l !== '').length;
    toast.success(`${prop.label} pasted to ${Math.min(filled, (items || []).length)} rows`);
    setColumnPasteModal(null);
    setColumnPasteContent('');
  };

  const enhancedDeleteRow = (index) => {
    if (currentListId) {
      setCurrentListId(null);
      clearListSelection();
    }
    deleteRow(index);
  };

  const handleListSelection = async (selectedKey) => {
    const selectedConfig = list.find(
      (item) => Object.keys(item)[0] === selectedKey
    );

    if (!selectedConfig) return;

    const listItems = selectedConfig[selectedKey];
    const previousListId = currentListId;

    const questionNumber =
      selectedVariable?.Question_number || selectedTitle;

    const typeMap = {
      Rows: "rows",
      Columns: "columns",
      Statements: "statements",
    };
    const currentType = typeMap[label];

    /* ---------------------------------------------
       STEP 1: Decide replace / prepend / append
    --------------------------------------------- */

    let applyMode = "replace";

    if (items && items.length > 0) {
      const choice = window.prompt(
        `How would you like to apply this list?\n\n` +
        `1 → Replace existing ${label}\n` +
        `2 → Append at beginning\n` +
        `3 → Append at end\n\n` +
        `Enter 1, 2 or 3`,
        "1"
      );

      if (!["1", "2", "3"].includes(choice)) {
        toast.warning("Invalid choice. Action cancelled.");
        return;
      }

      applyMode =
        choice === "1"
          ? "replace"
          : choice === "2"
            ? "prepend"
            : "append";
    } else {
      const ok = await confirm({
        title: "Add List",
        message: `There are no existing ${label.toLowerCase()}. Are you sure you want to add this list here?`,
        confirmText: "Add",
      });
      if (!ok) return;
    }

    /* ---------------------------------------------
       STEP 2: Remove from previous list if needed
    --------------------------------------------- */

    if (previousListId && previousListId !== selectedConfig.id) {
      setList((prevList) =>
        prevList
          .map((item) => {
            if (item.id === previousListId) {
              const qNums = item.questionNumbers || [];
              const updatedQNums = qNums.filter(
                (q) =>
                  !(
                    q.name === questionNumber &&
                    q.type === currentType
                  )
              );

              if (updatedQNums.length === 0) return null;

              const names = [...new Set(updatedQNums.map((q) => q.name))].sort();
              const types = [...new Set(updatedQNums.map((q) => q.type))];

              const typeIdentifier =
                types.length > 1
                  ? `[Mixed:${types.join("/")}]`
                  : `[${types[0]}]`;

              const newKey =
                names.length > 1
                  ? `${names.join("_")}${typeIdentifier}`
                  : `${names[0]}${typeIdentifier}`;

              return {
                [newKey]: item[Object.keys(item)[0]],
                type: item.type,
                questionNumbers: updatedQNums,
                usageCount: updatedQNums.length,
                id: item.id,
              };
            }
            return item;
          })
          .filter(Boolean)
      );
    }

    /* ---------------------------------------------
       STEP 3: Apply list to current items
    --------------------------------------------- */

    let updatedItems = [];

    if (applyMode === "replace") {
      updatedItems = listItems;
    } else if (applyMode === "prepend") {
      updatedItems = [...listItems, ...(items || [])];
    } else {
      updatedItems = [...(items || []), ...listItems];
    }

    savePropertyChanges(updatedItems, label);

    /* ---------------------------------------------
       STEP 4: Track list usage (UNCHANGED)
    --------------------------------------------- */

    setCurrentListId(selectedConfig.id);
    saveListSelection(selectedConfig.id);

    const currentQuestionNumbers =
      selectedConfig.questionNumbers || [];

    const alreadyLinked = currentQuestionNumbers.find(
      (q) => q.name === questionNumber && q.type === currentType
    );

    let displayKey = selectedKey;

    if (!alreadyLinked) {
      const updatedQuestionNumbers = [
        ...currentQuestionNumbers,
        { name: questionNumber, type: currentType },
      ];

      const names = [...new Set(updatedQuestionNumbers.map((q) => q.name))].sort();
      const types = [...new Set(updatedQuestionNumbers.map((q) => q.type))];

      const typeIdentifier =
        types.length > 1
          ? `[Mixed:${types.join("/")}]`
          : `[${types[0]}]`;

      const newKey =
        names.length > 1
          ? `${names.join("_")}${typeIdentifier}`
          : `${names[0]}${typeIdentifier}`;

      setList((prevList) =>
        prevList.map((item) => {
          if (Object.keys(item)[0] === selectedKey) {
            return {
              [newKey]: listItems,
              type: selectedConfig.type,
              questionNumbers: updatedQuestionNumbers,
              usageCount: updatedQuestionNumbers.length,
              id: selectedConfig.id,
            };
          }
          return item;
        })
      );

      displayKey = newKey;
    }

    /* ---------------------------------------------
       STEP 5: Final feedback
    --------------------------------------------- */

    const modeText =
      applyMode === "replace"
        ? "Replaced"
        : applyMode === "prepend"
          ? "Appended at beginning"
          : "Appended at end";

    toast.success(`List "${displayKey}" applied (${modeText}) to ${questionNumber}`);
  };

  // Enhanced list data for HoverDropdown with usage information
  const getEnhancedListData = () => {
    return list
      .map(item => {
        const key = Object.keys(item)[0];
        const isSelected = item.id === currentListId;

        return {
          value: key,
          label: (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
              padding: '4px 8px',
              borderRadius: '4px'
            }}>
              <span style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                {key}
              </span>
              <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: '#666' }}>
                <span>{item.usageCount || 1} question{item.usageCount > 1 ? 's' : ''}</span>
                <span>{item[key].length} items</span>
                {isSelected && <span>✅</span>}
              </div>
            </div>
          )
        };
      });
  };

  const openPropertiesModal = (item, index) => {
    setSelectedItem(item);
    setItemIndex(index);
    setIsModalOpen(true);
  };

  // Edits are matched by id when the item has one, falling back to the raw
  // array position (`index`, captured when the modal opened) only when it
  // doesn't. Matching by position alone is fragile: if anything else touches
  // `items` while the modal is open — or the item was just converted into a
  // header and given a fresh id — the position captured at open time can no
  // longer point at the right entry, so the edit silently lands on nothing
  // or the wrong row.
  const handlePropertyChange = (index, property, value) => {
    let patch = { [property]: value };
    // Converting a row into a header for the first time — give it a stable
    // id immediately so every later edit in this session (its row list,
    // label, unchecking it again) can be matched by identity.
    if (property === "header" && value === true && !selectedItem?.id) {
      patch = { ...patch, id: `hdr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` };
    }
    setSelectedItem((prev) => (prev ? { ...prev, ...patch } : prev));
    const targetId = selectedItem?.id;
    const updatedItems = (items || []).map((it, i) => {
      const isTarget = targetId != null ? it.id === targetId : i === index;
      return isTarget ? { ...it, ...patch } : it;
    });
    savePropertyChanges(updatedItems, label);
  };

  // Enhanced handleItemChange to detect when items no longer match selected list
  const handleItemChange = (index, value) => {
    const updatedItems = [...items];
    updatedItems[index].text = value;

    // Check if items still match the selected list
    if (currentListId) {
      const selectedList = list.find(item => item.id === currentListId);
      if (selectedList) {
        const selectedListItems = selectedList[Object.keys(selectedList)[0]];
        if (JSON.stringify(updatedItems) !== JSON.stringify(selectedListItems)) {
          // Items no longer match - clear selection
          setCurrentListId(null);
          clearListSelection();
        }
      }
    }

    savePropertyChanges(updatedItems, label);
  };

  // Enhanced addItem to clear list selection when manually adding items
  const addItem = () => {
    const newItem = {
      id: `${label === "Rows" ? "r" : "c"}${items ? items.length + 1 : 1}`,
      text: "",
    };

    const updatedItems = items ? [...items, newItem] : [newItem];

    // Clear list selection when manually adding items
    if (currentListId) {
      setCurrentListId(null);
      clearListSelection();
    }

    savePropertyChanges(updatedItems, label);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    // Draggable indices only span the real (non-header) rows — header rows
    // aren't rendered as draggable list items. Reorder the real rows among
    // themselves and keep the header entries as-is (appended at the tail);
    // what they cover is defined by start/end, not by array adjacency.
    const headerEntries = (items || []).filter((it) => it?.header);
    const reordered = Array.from(realItems);
    const [movedItem] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, movedItem);
    const updatedItems = [...reordered, ...headerEntries];

    if (typeof setItems === "function") {
      setItems(updatedItems);
    } else if (typeof savePropertyChanges === "function") {
      savePropertyChanges(updatedItems, label);
    } else {
      console.error("Neither setItems nor savePropertyChanges is available");
    }
  };

  const sortByIndexAscending = () => {
    // Separate headers and real items
    const headers = (items || []).filter(it => it?.header);
    const realItemsList = (items || []).filter(it => !it?.header);

    // Sort real items by their index (converting to number for proper numeric sorting)
    const sortedRealItems = [...realItemsList].sort((a, b) => {
      const indexA = parseInt(a.index) || 0;
      const indexB = parseInt(b.index) || 0;
      return indexA - indexB;
    });

    // Combine sorted real items with headers
    const updatedItems = [...sortedRealItems, ...headers];

    if (typeof setItems === "function") {
      setItems(updatedItems);
    } else if (typeof savePropertyChanges === "function") {
      savePropertyChanges(updatedItems, label);
    }
  };

  const sortByIndexDescending = () => {
    // Separate headers and real items
    const headers = (items || []).filter(it => it?.header);
    const realItemsList = (items || []).filter(it => !it?.header);

    // Sort real items by their index in descending order
    const sortedRealItems = [...realItemsList].sort((a, b) => {
      const indexA = parseInt(a.index) || 0;
      const indexB = parseInt(b.index) || 0;
      return indexB - indexA;
    });

    // Combine sorted real items with headers
    const updatedItems = [...sortedRealItems, ...headers];

    if (typeof setItems === "function") {
      setItems(updatedItems);
    } else if (typeof savePropertyChanges === "function") {
      savePropertyChanges(updatedItems, label);
    }
  };

  const cleanPastedHTML = (dirty) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, "text/html");

    // Allowed inline and block formatting tags — no table tags!
    const allowedTags = [
      "B",
      "I",
      "U",
      "STRONG",
      "EM",
      "BR",
      "P",
      "DIV",
      "OL",
      "UL",
      "LI",
      "SPAN",
    ];

    const allowedStyles = [
      "color",
      "background-color",
      "font-weight",
      "font-style",
      "text-decoration",
    ];

    const cleanNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Clean text content - replace multiple spaces with single space
        // and handle Word's non-breaking spaces
        let text = node.textContent
          .replace(/\u00A0/g, " ") // Replace non-breaking spaces with regular spaces
          .replace(/\s+/g, " ") // Replace multiple spaces with single space
          .trim(); // Trim whitespace

        // Only return text if it's not empty after cleaning
        return text || "";
      }

      // ❌ If it's a table-related tag, flatten it (remove structure, keep content)
      if (["TABLE", "TBODY", "TR", "TD", "TH"].includes(node.nodeName)) {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .filter((text) => text.trim()) // Remove empty strings
          .join(" ")
          .replace(/\s{2,}/g, " ")
          .trim();
        return content;
      }

      // Handle ordered lists - convert to individual items
      if (node.nodeName === "OL") {
        const listItems = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const content = Array.from(li.childNodes)
              .map(cleanNode)
              .join("")
              .trim();
            return content ? `<div>${content}</div>` : "";
          })
          .filter((item) => item)
          .join("");
        return listItems;
      }

      // Handle unordered lists - convert to individual items
      if (node.nodeName === "UL") {
        const listItems = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const content = Array.from(li.childNodes)
              .map(cleanNode)
              .join("")
              .trim();
            return content ? `<div>${content}</div>` : "";
          })
          .filter((item) => item)
          .join("");
        return listItems;
      }

      // Handle list items - clean them individually
      if (node.nodeName === "LI") {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .join("")
          .trim();
        return content ? `<div>${content}</div>` : "";
      }

      // Remove Word-specific classes and empty spans
      if (
        node.nodeName === "SPAN" &&
        (!node.innerHTML.trim() || node.classList.contains("MsoNormal"))
      ) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      // Remove Word-specific comments and processing instructions
      if (
        node.nodeType === Node.COMMENT_NODE ||
        node.nodeType === Node.PROCESSING_INSTRUCTION_NODE
      ) {
        return "";
      }

      // Remove Word's style definitions and scripts
      if (
        node.nodeName === "STYLE" ||
        node.nodeName === "SCRIPT" ||
        node.nodeName === "META"
      ) {
        return "";
      }

      // If tag not allowed — unwrap it but keep children
      if (!allowedTags.includes(node.nodeName)) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      // Clean up Word's class attributes
      if (node.hasAttribute("class")) {
        const classes = node.getAttribute("class").split(" ");
        const wordClasses = ["MsoNormal", "MsoListParagraph", "MsoBodyText"];
        const filteredClasses = classes.filter(
          (cls) => !wordClasses.includes(cls)
        );
        if (filteredClasses.length > 0) {
          node.setAttribute("class", filteredClasses.join(" "));
        } else {
          node.removeAttribute("class");
        }
      }

      // Remove Word-specific attributes
      const wordAttributes = ["lang", "style", "type", "start"];
      wordAttributes.forEach((attr) => {
        if (node.hasAttribute(attr)) {
          node.removeAttribute(attr);
        }
      });

      // Keep only allowed inline styles
      if (node.style && node.style.length > 0) {
        const styles = [];
        allowedStyles.forEach((styleProp) => {
          const value = node.style.getPropertyValue(styleProp);
          if (value) styles.push(`${styleProp}: ${value}`);
        });
        if (styles.length > 0) {
          node.setAttribute("style", styles.join("; "));
        } else {
          node.removeAttribute("style");
        }
      }

      // Recursively clean children
      const childContent = Array.from(node.childNodes)
        .map(cleanNode)
        .filter((text) => text.trim()) // Remove empty text nodes
        .join("");

      // If no content after cleaning, return empty string
      if (!childContent.trim()) {
        return "";
      }

      // For paragraphs and divs, only wrap if they have content
      if (["P", "DIV"].includes(node.nodeName) && childContent.trim()) {
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      // For inline elements, only wrap if they have content
      if (
        ["B", "I", "U", "STRONG", "EM", "SPAN"].includes(node.nodeName) &&
        childContent.trim()
      ) {
        // Only keep span if it has meaningful styles
        if (node.nodeName === "SPAN" && !node.getAttribute("style")) {
          return childContent;
        }
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      return childContent;
    };

    let cleanedContent = Array.from(doc.body.childNodes)
      .map(cleanNode)
      .filter((text) => text.trim()) // Remove empty nodes
      .join("");

    // Additional processing for numbered lists in plain text format
    if (!cleanedContent.includes("<div>") && !cleanedContent.includes("<li>")) {
      // Split by common list patterns
      const lines = cleanedContent.split(/\n/);
      const processedLines = lines
        .map((line) => {
          // Remove numbered list markers (1., 2., etc.)
          line = line.replace(/^\s*\d+\.\s*/, "").trim();
          // Remove bullet points
          line = line.replace(/^[•\-]\s*/, "").trim();
          return line;
        })
        .filter((line) => line.trim());

      if (processedLines.length > 1) {
        cleanedContent = processedLines
          .map((line) => `<div>${line}</div>`)
          .join("");
      }
    }

    // Final cleanup
    cleanedContent = cleanedContent
      .replace(/\n\s*\n/g, "\n") // Replace multiple newlines with single newline
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br><br>") // Limit consecutive <br> tags
      .trim();

    return cleanedContent || "";
  };
  const isModalOpenRef = useRef(isModalOpen);

  useEffect(() => {
    isModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);
  // Handle global paste event
  const handleGlobalPaste = (e) => {
    // Check if the paste target is inside our component
    if (isModalOpenRef.current) return;
    if (columnPasteModalRef.current) return;
    // Don't intercept pastes into inline property inputs
    if (e.target && e.target.getAttribute && e.target.getAttribute('data-inline-prop')) return;

    if (containerRef.current && containerRef.current.contains(e.target)) {
      e.preventDefault();

      // Get pasted content
      const htmlContent = e.clipboardData.getData("text/html");
      const plainContent = e.clipboardData.getData("text/plain");

      let cleaned;
      if (htmlContent) {
        cleaned = cleanPastedHTML(htmlContent);
      } else {
        cleaned = plainContent
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line)
          .map((line) => `<div>${line}</div>`)
          .join("");
      }

      // Open modal with the pasted content
      setPendingPaste({ pasteText: cleaned });
      setIsPasteModalOpen(true);
    }
  };

  // Handle paste from the modal
  const handleTextBoxPaste = (mode, preserveIndices) => {
    if (!pendingPaste?.pasteText?.trim() && !pendingPaste?.pasteText?.replace(/<[^>]*>/g, "").trim()) {
      toast.warning("Please paste some text first");
      return;
    }

    if (
      (mode === "insertBefore" || mode === "insertAfter") &&
      focusedRowIndex === null
    ) {
      toast.warning("Please click on a row first to set the insertion point");
      return;
    }

    // 🧹 Clean the pasted HTML before using it
    const cleanedHTML = cleanPastedHTML(pendingPaste.pasteText);

    // Create a synthetic paste event with sanitized HTML
    const syntheticEvent = {
      preventDefault: () => { },
      clipboardData: {
        getData: (type) => {
          if (type === "text/html") {
            return cleanedHTML || "";
          }
          // Extract plain text from cleaned HTML if needed
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = cleanedHTML;
          return tempDiv.textContent || tempDiv.innerText || "";
        },
      },
      pasteMode: mode,
      skipModal: true,
      insertIndex: focusedRowIndex,
      preserveOriginalIndices: preserveIndices
    };

    // ✅ Call the main paste handler
    handlePaste(syntheticEvent, label, mode);

    // Close modal and clear pending paste
    setIsPasteModalOpen(false);
    setPendingPaste(null);
  };

  const handlePasteContent = (html) => {

    setPendingPaste({ pasteText: html });
  };

  // Add and remove paste event listener
  useEffect(() => {
    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [focusedRowIndex]); // Re-add if focusedRowIndex changes

  // Real (non-header) rows, paired with their position in the raw `items`
  // array (rawIndex — used for everything that mutates `items` in place, e.g.
  // deleteRow/savePropertyChanges) and their position among real rows only
  // (realIndex — used for Draggable ordering, and the same numbering headers'
  // `indexes` are expressed in). Headers already carry their own rawIndex
  // (see the `headers` computation above), so a header row can also be
  // edited/deleted by its raw position without any id lookup.
  const realRowEntries = [];
  (items || []).forEach((item, rawIndex) => {
    if (item?.header) return;
    realRowEntries.push({ item, rawIndex, realIndex: realRowEntries.length });
  });

  // A header "contains" another header when its indexes are a proper subset
  // of the other's — that containment relationship is what drives
  // indentation in the list, so nested headers (and the real rows under
  // them) read as a tree at a glance. An empty header contains nothing.
  const headerContains = (outer, inner) =>
    outer.id !== inner.id &&
    inner.indexes.length > 0 &&
    inner.indexes.every((i) => outer.indexes.includes(i)) &&
    outer.indexes.length > inner.indexes.length;

  const headerDepth = (h) => headers.filter((h2) => headerContains(h2, h)).length;
  const rowDepth = (realIndex) => headers.filter((h) => h.indexes.includes(realIndex)).length;
  // Anchor position used to decide where a header renders in the list —
  // immediately above the first real row it covers. Indexes needn't be
  // contiguous, so this is just the smallest one.
  const headerAnchor = (h) => (h.indexes.length ? h.indexes[0] : Infinity);

  // Build one ordered display list mixing header rows and real rows: each
  // header renders immediately above the first real row it covers (outermost
  // headers first when several anchor at the same row), so headers show up
  // "within rows" instead of off to the side. A header with no rows selected
  // yet (indexes is empty) is shown last rather than silently dropped.
  const headersSorted = [...headers].sort((a, b) => {
    const aa = headerAnchor(a), ab = headerAnchor(b);
    if (aa !== ab) return aa - ab;
    return b.indexes.length - a.indexes.length; // wider (outer) coverage first
  });
  const headersByStart = {};
  headersSorted.forEach((h) => {
    const anchor = headerAnchor(h);
    (headersByStart[anchor] = headersByStart[anchor] || []).push(h);
  });

  // Note: each header (`h`) already carries its own `.rawIndex` from the
  // `headers` computation above — no separate lookup needed here.
  const displayEntries = [];
  const emittedHeaderIds = new Set();
  realRowEntries.forEach(({ item, rawIndex, realIndex }) => {
    (headersByStart[realIndex] || []).forEach((h) => {
      displayEntries.push({ kind: "header", header: h, depth: headerDepth(h) });
      emittedHeaderIds.add(h.id);
    });
    displayEntries.push({ kind: "row", item, rawIndex, realIndex, depth: rowDepth(realIndex) });
  });
  headersSorted.forEach((h) => {
    if (emittedHeaderIds.has(h.id)) return;
    displayEntries.push({ kind: "header", header: h, depth: headerDepth(h) });
  });

  return (
    <div ref={containerRef} style={{ display: "flex", gap: "20px", width: "100%" }}>

      {/* Left side - existing rows/columns component */}
      <div
        className={`${label.toLowerCase()}-section`}
        style={{ flex: "1", minWidth: "400px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <h4 style={{ margin: "0" }}>{label}</h4>
          {items && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
              <CommonButton symbol={faArrowUp} title="Sort Ascending (1 to N)" onClick={sortByIndexAscending} variant="primary" size="small" />
              <CommonButton symbol={faArrowDown} title="Sort Descending (N to 1)" onClick={sortByIndexDescending} variant="primary" size="small" />
              <CommonButton label="Save List" title="Save to List" onClick={() => addList(label)} variant="success" size="small" />
              <CommonButton
                symbol={faEye}
                label="Advanced Properties"
                title="Configure visible property columns"
                onClick={() => setConfigOpen(v => !v)}
                variant="secondary"
                size="small"
              />
              {/* Same popup chrome as the Survey Builder's Global Settings /
                  Question Settings popups (backdrop + centered panel) -
                  see .popup-modal-* in SurveyBuilder.css. */}
              {configOpen && (
                <div className="popup-modal-backdrop" onClick={() => setConfigOpen(false)}>
                  <div className="popup-modal-panel" onClick={(e) => e.stopPropagation()}>
                    <div className="popup-modal-header">
                      <div className="popup-modal-header-left">
                        <FontAwesomeIcon icon={faEye} className="popup-modal-icon" />
                        <span className="popup-modal-title">
                          Columns{qType ? ` for ${qType}` : ''}
                        </span>
                      </div>
                      <button className="popup-modal-close-btn" onClick={() => setConfigOpen(false)} title="Close">
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                    <div className="popup-modal-body">
                      {availablePropertiesForType.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-gray-500, #6b7280)' }}>
                          No optional property columns apply to this question type.
                        </p>
                      ) : (
                        availablePropertiesForType.map(prop => (
                          <label
                            key={prop.key}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13.5px', userSelect: 'none' }}
                          >
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(prop.key)}
                              onChange={() => toggleColumn(prop.key)}
                              style={{ cursor: 'pointer' }}
                            />
                            {prop.label}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
              {headersEnabled && (
                <CommonButton
                  symbol={faPlus}
                  label="Header"
                  title="Add a header row spanning a range of rows — open its settings to set Start/End"
                  onClick={addHeader}
                  disabled={!canAddHeader}
                  variant="secondary"
                  size="small"
                />
              )}

            </div>
          )}
          {list && list.length > 0 && (<div style={{ marginBottom: '15px' }}>
            <HoverDropdown
              text={currentListId ? "✓ List Selected" : "Select List"}
              exportfeatures={getEnhancedListData()}
              feature={6}
              handleSelection={handleListSelection}
              variant="modal"
            />

            {/* Show current list status */}
            {currentListId && (
              <div style={{
                fontSize: '12px',
                color: '#28a745',
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#d4edda',
                borderRadius: '4px',
                border: '1px solid #c3e6cb'
              }}>
                ✅ Linked to shared list. Changes here will update all questions using this list {currentListId}.
              </div>
            )}
          </div>)}
        </div>
        {/* List Selection with Enhanced Display */}

        <div style={{ overflowX: 'auto' }}>
          {/* Property column headers row */}
          {visibleColumnDefs.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', width: 'fit-content', minWidth: '100%', borderBottom: '2px solid #dee2e6', marginBottom: '4px', paddingBottom: '4px' }}>
              <div style={{ flex: '1 1 300px', minWidth: 300, fontSize: '12px', fontWeight: '600', color: '#6c757d', paddingLeft: 6 }}>
                {label} Text
              </div>
              {visibleColumnDefs.map(prop => (
                <div
                  key={prop.key}
                  style={{ width: prop.width, minWidth: prop.width, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', padding: '0 2px' }}
                >
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#495057', textAlign: 'center' }}>{prop.label}</span>
                  <button
                    title={`Paste ${prop.label} values from Excel (one per line)`}
                    onClick={() => { setColumnPasteModal(prop); setColumnPasteContent(''); }}
                    style={{
                      background: 'none', border: '1px solid #ced4da', borderRadius: '3px',
                      cursor: 'pointer', padding: '1px 5px', fontSize: '10px', color: '#495057',
                      display: 'flex', alignItems: 'center', gap: '3px',
                    }}
                  >
                    <FontAwesomeIcon icon={faClipboard} /> paste
                  </button>
                </div>
              ))}
            </div>
          )}

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={label.toLowerCase()}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{
                    backgroundColor: snapshot.isDraggingOver
                      ? "#f8f9fa"
                      : "transparent",
                    borderRadius: "4px",
                    padding: "4px",
                    minHeight: "100px",
                  }}
                >
                  {displayEntries.map((entry) => {
                    if (entry.kind === "header") {
                      const h = entry.header;
                      return (
                        <div
                          key={h.id}
                          className="row-item header-row-item"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginLeft: entry.depth * INDENT_PX,
                            background: "#e6fbf8",
                            border: "1px solid #99e6d9",
                            borderLeft: entry.depth > 0 ? "3px solid #0f766e" : "1px solid #99e6d9",
                            borderRadius: "4px",
                            padding: "6px 10px",
                            marginBottom: "6px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#0f766e",
                              textTransform: "uppercase",
                              letterSpacing: 0.3,
                              border: "1px solid #0f766e",
                              borderRadius: 3,
                              padding: "1px 5px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Header
                          </span>
                          <input
                            type="text"
                            value={h.text || ""}
                            placeholder="Header label"
                            onChange={(e) => updateHeaderField(h.id, { text: e.target.value })}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              border: "1px solid #99e6d9",
                              borderRadius: 4,
                              padding: "6px 8px",
                              fontSize: 14,
                              background: "#fff",
                              fontWeight: 600,
                              color: "#0f766e",
                            }}
                          />
                          <span style={{ fontSize: 12, color: "#0f766e", whiteSpace: "nowrap" }}>
                            rows {formatRowNumbers(h.indexes)}
                          </span>
                          <CommonButton
                            label=""
                            title="Header settings"
                            symbol={faCog}
                            onClick={() => openPropertiesModal(h, h.rawIndex)}
                          />
                          <CommonButton
                            label=""
                            symbol={faTrashAlt}
                            title="Delete header"
                            onClick={() => deleteHeader(h.id)}
                            variant="danger"
                          />
                        </div>
                      );
                    }

                    const { item, rawIndex, realIndex, depth } = entry;
                    const isTerminated =
                      screenOutSelections?.[rawIndex] || item.screenout || false;

                    return (
                      <Draggable
                        key={rawIndex}
                        draggableId={`${label.toLowerCase()}-${realIndex}`}
                        index={realIndex}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`row-item ${snapshot.isDragging ? "dragging" : ""
                              }`}
                            style={{
                              ...provided.draggableProps.style,
                              // Content-driven width (instead of stretching to
                              // 100% of the scroll container) so that once enough
                              // Advanced Properties columns are visible to not
                              // fit, the row grows past the container and the
                              // wrapper's overflowX:auto scrolls horizontally -
                              // rather than every flex child (including the
                              // FormattableInput below) being squeezed to fit.
                              width: "fit-content",
                              minWidth: "100%",
                              marginLeft: depth * INDENT_PX,
                              borderLeft: depth > 0 ? "3px solid #cbd5e1" : undefined,
                              backgroundColor:
                                focusedRowIndex === rawIndex
                                  ? "#f1f1f1"
                                  : snapshot.isDragging
                                    ? "#f1f1f1"
                                    : "#f9f9f9",
                            }}
                            onClick={() => setFocusedRowIndex(rawIndex)}
                          >
                            <input
                              className="row-index-input"
                              type="text"
                              value={item.index ?? ""}
                              title="Edit row index"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handleInlinePropertyChange(
                                  rawIndex,
                                  "index",
                                  e.target.value
                                )
                              }
                            />
                            <div
                              className="row-grip"
                              {...provided.dragHandleProps}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <FontAwesomeIcon icon={faGripLines} />
                            </div>

                            <FormattableInput
                              value={item.text}
                              onChange={(e) => {
                                handleItemChange(rawIndex, e.target.value);
                              }}
                              onClick={() => setFocusedRowIndex(rawIndex)}
                              onFocus={() => setFocusedRowIndex(rawIndex)}
                              type="contentEditable"
                              style={{
                                // "200 !important" isn't valid CSS - inline
                                // style values can't carry !important, and
                                // "200" (no unit) is dropped entirely, so this
                                // was never actually applying any floor. A
                                // flex-basis of 200px (paired with flexShrink:
                                // 0) gives it a real minimum instead - it grows
                                // to fill extra space but never shrinks below
                                // that, no matter how many Advanced Properties
                                // columns are toggled on to its right.
                                flex: "1 1 200px",
                                minWidth: "200px",
                                flexShrink: 0,
                                border: "1px solid #ccc",
                                padding: "8px",
                                borderRadius: "4px",
                                outline: "none",
                                backgroundColor: "#fff",
                                fontSize: "14px",
                                marginRight: "12px",
                                transition: "border-color 0.3s ease",
                              }}
                            />


                            <CommonButton label="" title="Settings" symbol={faCog} onClick={(e) => {
                              e.stopPropagation();
                              openPropertiesModal(item, rawIndex);
                            }} />

                            <label
                              className="terminate-checkbox"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isTerminated}
                                onChange={(e) =>
                                  onScreenOutChange(rawIndex, e.target.checked)
                                }
                              />
                              T
                            </label>
                            <CommonButton label="" symbol={faTrashAlt} title="Delete Question" onClick={(e) => {
                              e.stopPropagation();
                              deleteRow(rawIndex);
                            }} variant="danger" />
                            {visibleColumnDefs.map(prop => (
                              <div
                                key={prop.key}
                                style={{ width: prop.width, minWidth: prop.width, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', flexShrink: 0 }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {prop.type === 'checkbox' ? (
                                  <input
                                    type="checkbox"
                                    // Most checkboxes here default unchecked when the
                                    // row has never set the key. `defaultTrue` (only
                                    // otherRequired today) flips that default to
                                    // checked, matching the same default the
                                    // properties popup's checkbox uses.
                                    checked={item[prop.key] !== undefined ? !!item[prop.key] : !!prop.defaultTrue}
                                    onChange={(e) => handleInlinePropertyChange(rawIndex, prop.key, e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ) : prop.type === 'color' ? (
                                  // Swatch + hex text side by side - the swatch drives the
                                  // native picker, the text field is what accepts a pasted
                                  // Excel column (via handleInlinePaste, same as any other
                                  // text column) or a manually typed hex code. Both write to
                                  // the same `color` key that the properties popup edits, so
                                  // the two stay in sync automatically.
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <input
                                      type="color"
                                      title={item[prop.key] || 'Pick a color'}
                                      value={normalizeHexForPicker(item[prop.key])}
                                      onChange={(e) => handleInlinePropertyChange(rawIndex, prop.key, e.target.value)}
                                      style={{ width: 26, height: 26, padding: 1, border: '1px solid #ced4da', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}
                                    />
                                    <input
                                      type="text"
                                      data-inline-prop="true"
                                      placeholder="#RRGGBB"
                                      value={item[prop.key] || ''}
                                      onChange={(e) => handleInlinePropertyChange(rawIndex, prop.key, e.target.value)}
                                      onPaste={(e) => handleInlinePaste(e, rawIndex, prop.key)}
                                      style={{ width: '100%', minWidth: 0, border: '1px solid #ced4da', borderRadius: '3px', padding: '3px 5px', fontSize: '12px', boxSizing: 'border-box' }}
                                    />
                                  </div>
                                ) : prop.type === 'select' ? (
                                  // Unset defaults to "text", matching the same
                                  // default the Row/Column Properties popup's
                                  // Input Type dropdown falls back to.
                                  <select
                                    data-inline-prop="true"
                                    value={item[prop.key] || 'text'}
                                    onChange={(e) => handleInlinePropertyChange(rawIndex, prop.key, e.target.value)}
                                    style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '3px', padding: '3px 5px', fontSize: '12px', boxSizing: 'border-box' }}
                                  >
                                    {prop.options.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    data-inline-prop="true"
                                    value={item[prop.key] || ''}
                                    onChange={(e) => handleInlinePropertyChange(rawIndex, prop.key, e.target.value)}
                                    onPaste={(e) => handleInlinePaste(e, rawIndex, prop.key)}
                                    style={{ width: '100%', border: '1px solid #ced4da', borderRadius: '3px', padding: '3px 5px', fontSize: '12px', boxSizing: 'border-box' }}
                                  />
                                )}
                              </div>
                            ))}

                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>{/* end overflowX wrapper */}

        <CommonButton label={`+ New ${label}`} title={`Add new ${label}`} onClick={() => addadditionalRow(label)} variant="tab" />

        <RowColumnProperties
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          item={selectedItem}
          index={itemIndex}
          handlePropertyChange={handlePropertyChange}
          qType={qType}
        />

        {/* Column Paste Modal - same popup-modal-* chrome as the Columns
            popup above and Survey Builder's Global Settings popup. */}
        {columnPasteModal && (
          <div className="popup-modal-backdrop" onClick={() => setColumnPasteModal(null)}>
            <div className="popup-modal-panel" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
              <div className="popup-modal-header">
                <div className="popup-modal-header-left">
                  <FontAwesomeIcon icon={faClipboard} className="popup-modal-icon" />
                  <span className="popup-modal-title">Paste {columnPasteModal.label} Values</span>
                </div>
                <button className="popup-modal-close-btn" onClick={() => setColumnPasteModal(null)} title="Close">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="popup-modal-body">
                <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--color-gray-500, #6b7280)', lineHeight: 1.5 }}>
                  Copy a column from Excel and paste below. One value per row — fills rows in order (top to bottom).
                  {columnPasteModal.type === 'checkbox' && ' Use: true/1/yes/x to check, anything else to uncheck.'}
                </p>
                <textarea
                  value={columnPasteContent}
                  onChange={(e) => setColumnPasteContent(e.target.value)}
                  onPaste={(e) => e.stopPropagation()}
                  rows={10}
                  autoFocus
                  placeholder="Paste Excel column data here…"
                  style={{
                    minWidth:'200px', width: '100%', border: '1px solid #d1d5db', borderRadius: '6px',
                    padding: '10px 12px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box',
                    fontFamily: 'ui-monospace, Consolas, Monaco, monospace',
                  }}
                />
              </div>
              <div className="popup-modal-footer">
                <CommonButton label="Cancel" onClick={() => { setColumnPasteModal(null); setColumnPasteContent(''); }} variant="secondary" />
                <CommonButton label="Apply to Rows" onClick={applyColumnPaste} variant="success" />
              </div>
            </div>
          </div>
        )}

        {/* Paste Modal */}
        {isPasteModalOpen && (
          <PasteModal
            isOpen={isPasteModalOpen}
            onClose={() => {
              setIsPasteModalOpen(false);
              setPendingPaste(null);
            }}
            onPaste={handlePasteContent}
            onAction={handleTextBoxPaste}
            label={label}
            focusedRowIndex={focusedRowIndex}
            initialContent={pendingPaste?.pasteText || ""}
            preserveOriginalIndices={preserveOriginalIndices}
            onPreserveIndicesChange={setPreserveOriginalIndices}
          />
        )}
      </div>

    </div>
  );
};

export default RowColumnComponent;